import Company from '@/App/Company/Entity/Company'
import CompanyUserAggregate from '@/App/CompanyUser/Entity/CompanyUserAggregate'
import Integration from '@/App/Integration/Entity/Integration'
import User from '@/App/User/Entity/User'
import MeetingData from '@/App/Booking/Meeting/Entity/MeetingData'
import Service from '@/App/Service/Entity/Service'
import ServiceAggregate from '@/App/Service/Entity/ServiceAggregate'
import ApiFormErrors from '@/Shared/Component/Forms/ApiFormErrors'
import {
    type ResponseJsonKeyEnum,
    type QueueStatsDataType,
    type PaginatedResultsType,
    type CompanyData,
    type CompanyUserAggregateData,
    type IntegrationData,
    type UserData,
    type MeetingDataRawData,
    type BookingDataRawDataApp,
    type BookingDataRawDataMeeting,
    type AvailabilityData,
    type ServiceData,
    type ServiceAggregateData,
    type AutomationRawData,
} from '@/Shared/AutoGenerated/Main'
import Availability from '@/App/Availability/Entity/Availability'
import InPersonHostDefinedMeetingLocation from '@/App/Company/Entity/InPersonHostDefinedMeetingLocation'
import BookingDataApp from '@/App/Booking/Booking/Entity/BookingData'
import BookingDataMeeting from '@/Meeting/Booking/Entity/BookingData'
import type {
    RequestBodyKeyEnum,
    RequestFileKeyEnum,
} from '@/Shared/AutoGenerated/RequestFieldEnum'
import Automation from '@/App/Automation/Entity/Automation'

export default class ApiFormResponseType {
    constructor(private readonly data: any) {}

    getMessage(): string | null {
        const data = this.data

        if (typeof data !== 'object') {
            return null
        }

        const message = data.message

        if (typeof message === 'string') {
            return message
        }

        return null
    }

    getObjectFromPayload(key: ResponseJsonKeyEnum): object {
        const data = this.data

        if (typeof data !== 'object') {
            throw new Error('Data should be an object')
        }

        const payload = data.payload

        if (typeof payload !== 'object') {
            throw new Error('Payload should be an object')
        }

        if (!(key in payload)) {
            throw new Error(`Key '${key}' not found in payload`)
        }

        const content = payload[key] ?? null

        if (typeof content !== 'object') {
            throw new Error('Content should be an object')
        }

        return content
    }

    getStringFromPayload(key: ResponseJsonKeyEnum): string {
        const data = this.data

        if (typeof data !== 'object') {
            throw new Error('Data should be an object')
        }

        const payload = data.payload

        if (typeof payload !== 'object') {
            throw new Error('Payload should be an object')
        }

        if (!(key in payload)) {
            throw new Error(`Key '${key}' not found in payload`)
        }

        const content = payload[key] ?? null

        if (typeof content !== 'string') {
            throw new Error('Content should be a string')
        }

        return content
    }

    getArrayFromPayload(key: ResponseJsonKeyEnum): any[] {
        const data = this.data

        if (typeof data !== 'object') {
            throw new Error('Data should be an object')
        }

        const payload = data.payload

        if (typeof payload !== 'object') {
            throw new Error('Payload should be an object')
        }

        if (!(key in payload)) {
            throw new Error(`Key '${key}' not found in payload`)
        }

        const content = payload[key] ?? null

        if (!Array.isArray(content)) {
            throw new Error('Content should be an array')
        }

        return content
    }

    getErrors(): ApiFormErrors {
        return new ApiFormErrors(this.data?.errors ?? {})
    }

    /**
     * Shortcut to getErrors().getValidationError()
     */
    getValidationError(
        key: RequestBodyKeyEnum | RequestFileKeyEnum,
    ): string | undefined {
        return this.getErrors().getValidationError(key)
    }

    // ------------------------------------------------
    // Hydrator helper methods
    // ------------------------------------------------

    getHydratedPaginatedResults(
        key: ResponseJsonKeyEnum,
    ): PaginatedResultsType {
        const data = this.getObjectFromPayload(key)

        return data as PaginatedResultsType
    }

    getHydratedQueueStats(key: ResponseJsonKeyEnum): QueueStatsDataType {
        const data = this.getObjectFromPayload(key)

        return data as QueueStatsDataType
    }

    getHydratedCompany(key: ResponseJsonKeyEnum): Company {
        const data = this.getObjectFromPayload(key)

        return Company.hydrateSingle(data as CompanyData)
    }

    getHydratedIntegration(key: ResponseJsonKeyEnum): Integration {
        const data = this.getObjectFromPayload(key)

        return Integration.hydrateSingle(data as IntegrationData)
    }

    getHydratedService(key: ResponseJsonKeyEnum): Service {
        const data = this.getObjectFromPayload(key)

        return Service.hydrateSingle(data as ServiceData)
    }

    getHydratedServiceAggregate(key: ResponseJsonKeyEnum): ServiceAggregate {
        const data = this.getObjectFromPayload(key)

        return ServiceAggregate.hydrateSingle(data as ServiceAggregateData)
    }

    getHydratedCompanyUserAggregate(
        key: ResponseJsonKeyEnum,
    ): CompanyUserAggregate {
        const data = this.getObjectFromPayload(key)

        return CompanyUserAggregate.hydrateSingle(
            data as CompanyUserAggregateData,
        )
    }

    getHydratedUser(key: ResponseJsonKeyEnum): User {
        const data = this.getObjectFromPayload(key)

        return User.hydrateSingle(data as UserData)
    }

    getHydratedMeetingData(key: ResponseJsonKeyEnum): MeetingData {
        const data = this.getObjectFromPayload(key)

        return MeetingData.hydrateSingle(data as MeetingDataRawData)
    }

    getHydratedBookingDataForApp(key: ResponseJsonKeyEnum): BookingDataApp {
        const data = this.getObjectFromPayload(key)

        return BookingDataApp.hydrateSingle(data as BookingDataRawDataApp)
    }

    getHydratedBookingDataForMeeting(
        key: ResponseJsonKeyEnum,
    ): BookingDataMeeting {
        const data = this.getObjectFromPayload(key)

        return BookingDataMeeting.hydrateSingle(
            data as BookingDataRawDataMeeting,
        )
    }

    getHydratedAvailability(key: ResponseJsonKeyEnum): Availability {
        const data = this.getObjectFromPayload(key)

        return Availability.hydrateSingle(data as AvailabilityData)
    }

    getHydratedInPersonHostDefinedMeetingLocations(
        key: ResponseJsonKeyEnum,
    ): InPersonHostDefinedMeetingLocation[] {
        const data = this.getArrayFromPayload(key)

        return InPersonHostDefinedMeetingLocation.hydrateMultiple(data)
    }

    getHydratedAutomation(key: ResponseJsonKeyEnum): Automation {
        const data = this.getObjectFromPayload(key) as AutomationRawData

        return Automation.hydrateSingle(data)
    }
}
